/**
 * Courses and their chapters — the level between a degree and a module.
 *
 * Courses used to be a constant in modules.js, which meant renaming one, or
 * creating one on purpose rather than by accident, needed an engineer and a
 * deploy. They live in Firestore (`config/courses`) now and are edited on the
 * Courses admin page; what stays here is the shape, the seed used on a fresh
 * install, and the grouping logic, so the interesting part stays testable in
 * plain node. Degrees and departments made this same move first.
 *
 * CHAPTERS are the other half. `phase` was free text typed onto each module
 * with a colour beside it, and a `phases` array in modules.js listing which
 * module ids belonged to which — two copies of one fact, kept in step by a
 * test rather than by construction. A chapter now belongs to its COURSE, and a
 * module says which chapter it is in by name. Sheets Reporting needs six of
 * them for seventeen modules; the other three courses need none, and their old
 * phase name simply repeated the course name.
 *
 * Nothing here gates anything — see SECURITY-MODEL.md.
 */

import { SEED_COURSES, phases, modules } from './modules.js'
import { slugify } from './degrees.js'
import { isCourseVisible } from './catalogue.js'

export { slugify }

/**
 * Seed for a fresh install: today's courses, each carrying the chapters that
 * used to live in the `phases` array. Overwritten by whatever an admin saves.
 */
export const DEFAULT_COURSES = SEED_COURSES.map((track) => ({
  id: track.id,
  name: track.name,
  description: track.description,
  // The number this course opens at, carried over from the numbering it has
  // today rather than assumed to be 1. Sheets Reporting deliberately starts at
  // Module 0 — the big-picture "Where Does Our Data Come From?" — while the
  // other three already number exactly by position. Taking the minimum
  // reproduces the current numbering for every course instead of renumbering
  // seventeen Sheets modules under the trainees part-way through them.
  startsAt: (() => {
    const nums = modules
      .filter((m) => m.track === track.id && typeof m.num === 'number')
      .map((m) => m.num)
    return nums.length ? Math.min(...nums) : 1
  })(),
  chapters: phases
    .filter((phase) => phase.track === track.id)
    .map((phase) => ({ name: phase.name, color: phase.color })),
}))

export function findCourse(courseList, id) {
  return (courseList || []).find((c) => c.id === id) || null
}

export function courseChapters(course) {
  return Array.isArray(course?.chapters) ? course.chapters : []
}

/**
 * A course's modules, split into its chapters and in chapter order.
 *
 * `courseModules` arrives in catalogue order and stays in it inside each
 * chapter, which is what reproduces the hand-maintained ordering the `phases`
 * array used to carry.
 *
 * Anything whose `phase` matches no chapter lands in a trailing group rather
 * than vanishing. That case is the whole reason this is a function and not a
 * lookup: a module authored in AI Studio can name a chapter that does not
 * exist, and a module silently missing from the page is far worse than one
 * appearing under a heading nobody planned. An empty chapter is dropped, so a
 * course keeps its chapter list without showing headings over nothing.
 */
export function groupIntoChapters(course, courseModules = []) {
  const placed = new Set()
  const grouped = []

  for (const chapter of courseChapters(course)) {
    const modules = (courseModules || []).filter((m) => m.phase === chapter.name)
    if (modules.length === 0) continue
    modules.forEach((m) => placed.add(String(m.id)))
    grouped.push({ ...chapter, modules })
  }

  const unplaced = (courseModules || []).filter((m) => !placed.has(String(m.id)))
  if (unplaced.length > 0) {
    grouped.push({
      name: unplaced[0].phase || 'More in this course',
      color: unplaced[0].phaseColor || 'indigo',
      modules: unplaced,
      isUnplanned: true,
    })
  }

  return grouped
}

/**
 * Chapter names used by modules that the course does not declare — an admin
 * page warning, and the thing to look at when a module turns up under a
 * heading nobody wrote.
 */
export function unknownChapters(course, courseModules = []) {
  const known = new Set(courseChapters(course).map((c) => c.name))
  const seen = new Set()
  for (const mod of courseModules || []) {
    if (mod.phase && !known.has(mod.phase)) seen.add(mod.phase)
  }
  return [...seen]
}

/** Degrees that would be left pointing at nothing if this course went away. */
export function degreesUsingCourse(courseId, degreeList = []) {
  return (degreeList || []).filter((d) => (d.tracks || []).includes(courseId))
}

/** The URL of a course's own page. One place, so the word cannot drift again. */
/**
 * Where a module lives, READ IN A PARTICULAR COURSE.
 *
 * The course is in the path because a module can now belong to several, and
 * without it the page has to GUESS which one you came from — which showed in
 * the back link and in prev/next, and guessed wrong for any shared module.
 *
 * `/module/<id>` still works and still resolves; it redirects here. Falling
 * back to it (no course) is deliberate rather than an error: a module filed in
 * no course at all is still openable.
 */
export function moduleHref(courseId, moduleOrId) {
  if (moduleOrId === undefined || moduleOrId === null) return '/'
  // A module object gets its slug; a bare id stays an id. Both resolve, so a
  // caller that only holds an id is correct, just less readable in the bar.
  const key = typeof moduleOrId === 'object'
    ? (moduleOrId.slug || moduleOrId.id)
    : moduleOrId
  const mod = encodeURIComponent(String(key))
  return courseId ? `/courses/${encodeURIComponent(String(courseId))}/module/${mod}` : `/module/${mod}`
}

/**
 * A module title, as a slug.
 *
 * NOT `slugify` — that is for course and degree ids and cuts at 40 characters
 * wherever it lands, which is how "…Collaborative Saving System" became
 * "…collaborative-sav". A word chopped in half is worse than a longer address.
 *
 * Apostrophes are REMOVED rather than turned into separators, so "Team's"
 * reads "teams" and not "team-s". Everything else non-alphanumeric still
 * becomes a hyphen.
 *
 * Truncation lands on a word boundary: cut to the limit, then drop the partial
 * segment left dangling — unless that would leave nothing, in which case the
 * first word stands however long it is.
 */
export function moduleSlugBase(title, limit = 48) {
  const cleaned = String(title || '').toLowerCase().replace(/['\u2019]/g, '')
  const full = cleaned.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  if (!full) return ''
  if (full.length <= limit) return full
  const cut = full.slice(0, limit).replace(/-+$/, '')
  const boundary = cut.lastIndexOf('-')
  return boundary > 0 ? cut.slice(0, boundary) : cut
}

/**
 * A readable, permanent address for a module.
 *
 * MINTED ONCE, AT PUBLISH, AND NEVER RECOMPUTED. Renaming a module must not
 * change its URL — a link in a lesson, a bookmark, a message to a colleague all
 * outlive the title. That is why publishCustomModule only assigns this when
 * there is no slug yet, and why a rename does not touch it.
 *
 * Unique against every existing slug AND every existing id, because both are
 * addresses in the same space: a slug that collides with an id would shadow
 * a real module. Falls back to the id when a title has nothing sluggable in it.
 */
export function uniqueModuleSlug(title, existing = [], selfId = null) {
  const taken = new Set()
  for (const m of existing || []) {
    if (String(m.id) !== String(selfId)) {
      taken.add(String(m.id))
      if (m.slug) taken.add(String(m.slug))
    }
  }
  const base = moduleSlugBase(title)
  if (!base) return null
  if (!taken.has(base)) return base
  for (let n = 2; n < 100; n++) {
    const candidate = `${base}-${n}`
    if (!taken.has(candidate)) return candidate
  }
  return null
}

export function courseHref(courseId) {
  return courseId ? `/courses/${encodeURIComponent(courseId)}` : '/'
}

/**
 * Where an old `/modules` link should land.
 *
 * `/modules?track=<id>` was the course page, and `?track=` is the last place
 * the retired word "track" was still read by a human — it sat in the address
 * bar. The param is gone from everything the app writes, but CS bookmarks hold
 * it, so it is still ACCEPTED here and mapped to the course's own URL.
 * `?course=` is accepted too, for the short window it was emitted.
 *
 * Bare `/modules` used to list every module in every course on one page. That
 * page is gone: the course index is `/`, which is what it should always have
 * been — the flat list dated from when there was one course and `/modules`
 * simply meant "the modules".
 */
export function legacyModulesTarget(params) {
  const id = params?.get?.('course') || params?.get?.('track') || ''
  return id ? courseHref(id) : '/'
}


/* ─── Enrolment ─── */

/**
 * What a trainee gets before anyone assigns them anything. The generic Claude
 * material applies to everybody; the domain courses are assigned deliberately.
 */
export const DEFAULT_COURSE_IDS = ['claude-basics']

/**
 * The courses that count as this trainee's own — what an admin assigned them,
 * plus anything they have actually started.
 *
 * Three sources, in order of how deliberate they are: the courses carried by
 * every degree they hold (resolved by data/degrees, since degrees are editable
 * data rather than a constant), any course assigned to them individually — the
 * escape hatch for "in Finance, but also needs the Client Services course" —
 * and anything they have started of their own accord.
 *
 * Including started courses is what makes this safe to switch on. Eleven people
 * were part-way through the Sheets course with nothing assigned to them; had
 * "yours" meant only the assigned list, they would have signed in to find their
 * course gone and their progress apparently vanished. It also means someone who
 * wanders into another course and does it keeps it, which is the intent:
 * enrolment says what you are EXPECTED to do, never what you are allowed to do.
 *
 * IT TAKES THE COURSE LIST NOW, and that is the fix.
 *
 * This used to filter the hardcoded `SEED_COURSES` array in data/modules.js — the
 * four seed courses — so a course an admin created could NEVER be somebody's,
 * however deliberately it was given to them. Assigned directly: not yours.
 * Carried by your degree: not yours. It fell through to the default and the
 * course appeared under "Other courses", so a trainee saw a course they had
 * been enrolled in sitting in the browse-if-you-like pile. Courses became data
 * on 2026-08-31 and this was still reading the seed.
 *
 * Visibility is `isCourseVisible` rather than "has published built-in
 * modules", which is the same predicate Home, the degree maths and the course
 * page use — so a course whose modules are ALL custom now counts, and a course
 * an admin has hidden no longer does.
 *
 * Returned in the order the courses are declared, so the interface is stable.
 */
export function myCourses(
  { assigned = [], started = [], degreeCourses = [] } = {},
  courseList = DEFAULT_COURSES,
  customModules = [],
) {
  const mine = new Set(
    [...(degreeCourses || []), ...(assigned || []), ...(started || [])].map(String),
  )
  const chosen = (courseList || [])
    .filter((c) => mine.has(String(c.id)) && isCourseVisible(c, customModules))
    .map((c) => c.id)

  if (chosen.length > 0) return chosen
  return DEFAULT_COURSE_IDS.filter((id) =>
    isCourseVisible(findCourse(courseList, id), customModules),
  )
}

/** The visible courses that are not this trainee's — browsable, not theirs. */
export function otherCourses(mine = [], courseList = DEFAULT_COURSES, customModules = []) {
  const set = new Set((mine || []).map(String))
  return (courseList || [])
    .filter((c) => !set.has(String(c.id)) && isCourseVisible(c, customModules))
    .map((c) => c.id)
}
